import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  getLCAPhaseSelection,
  updateLCAPhaseSelection,
} from "../../../../../../apis/Product/LCAPhaseSelectionApi";
import "./index.css";

// Standard phases
const LCA_PHASES_STD = ["A1","A2","A3","A4","A5","B1","B2","B3","B4","B5","B6","B7","C1","C2","C3","C4","D"];
// Cradle-to-Cradle phases (C4 splits into C4_tech and C4_bio)
const LCA_PHASES_C2C = ["A1","A2","A3","A4","A5","B1","B2","B3","B4","B5","B6","B7","C1","C2","C3","C4_tech","C4_bio","D"];

const PHASE_LABELS_STD = {
  A1: "Raw material supply",
  A2: "Transport",
  A3: "Manufacturing",
  A4: "Transport",
  A5: "Construction & Installation",
  B1: "Use",
  B2: "Maintenance",
  B3: "Repair",
  B4: "Replacement",
  B5: "Refurbishment",
  B6: "Operational energy use",
  B7: "Operational water use",
  C1: "De-construction & demolition",
  C2: "Transport",
  C3: "Waste processing",
  C4: "Disposal",
  D: "Reuse-recovery-recycling potential",
};

const PHASE_LABELS_C2C = {
  A1: "Material supply",
  A2: "Transport",
  A3: "Manufacturing",
  A4: "Transport",
  A5: "Construction & Installation",
  B1: "Use",
  B2: "Maintenance",
  B3: "Repair",
  B4: "Replacement",
  B5: "Refurbishment",
  B6: "Operational energy use",
  B7: "Operational water use",
  C1: "Deconstruction",
  C2: "Transport",
  C3: "Resource handling",
  C4_tech: "Technological reuse",
  C4_bio: "Biological reuse",
  D: "Substitution effects",
};

const PHASE_CODE_DISPLAY = {
  C4_tech: "C4a",
  C4_bio: "C4b",
};

// BR18 scope: A1-A5, B4, B6, C3, C4, D
const BR18_PHASES = { A1: true, A2: true, A3: true, A4: true, A5: true, B4: true, B6: true, C3: true, C4: true, D: true };

const SCOPE_PRESETS = {
  "cradle-to-gate": { A1: true, A2: true, A3: true },
  "cradle-to-cradle": { A1: true, A2: true, A3: true, A4: true, A5: true, B1: true, B2: true, B3: true, B4: true, B5: true, B6: true, B7: true, C1: true, C2: true, C3: true, C4_tech: true, C4_bio: true, D: true },
  "cradle-to-grave": { A1: true, A2: true, A3: true, A4: true, A5: true, B1: true, B2: true, B3: true, B4: true, B5: true, B6: true, B7: true, C1: true, C2: true, C3: true, C4: true },
  "br18": BR18_PHASES,
};

function detectScope(phases) {
  if (!phases) return "custom";
  const keys = Object.keys(phases).filter((k) => phases[k]);
  if (keys.length === 3 && keys.includes("A1") && keys.includes("A2") && keys.includes("A3")) return "cradle-to-gate";
  // C2C: has C4_tech or C4_bio
  if (keys.includes("C4_tech") || keys.includes("C4_bio")) return "cradle-to-cradle";
  // BR18 check: exactly A1-A5, B4, B6, C3, C4, D (10 phases)
  const br18Keys = Object.keys(BR18_PHASES);
  if (keys.length === br18Keys.length && br18Keys.every((k) => phases[k])) return "br18";
  // Cradle-to-grave: all standard except D
  if (keys.length === 16 && !keys.includes("D") && !keys.includes("C4_tech") && !keys.includes("C4_bio")) return "cradle-to-grave";
  return "custom";
}

const SCOPE_OPTIONS = [
  { value: "cradle-to-gate", label: "Cradle to Gate" },
  { value: "cradle-to-cradle", label: "Cradle to Cradle" },
  { value: "cradle-to-grave", label: "Cradle to Grave" },
  { value: "br18", label: "BR18" },
  { value: "custom", label: "Custom" },
];

const LCAPhaseSelection = ({ productId }) => {
  const { t } = useTranslation();
  const [phases, setPhases] = useState({});
  const [lcaScope, setLcaScope] = useState("custom");
  const [hasChanges, setHasChanges] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const isCradleToCradle = lcaScope === "cradle-to-cradle";
  const phaseCodes = isCradleToCradle ? LCA_PHASES_C2C : LCA_PHASES_STD;
  const phaseLabels = isCradleToCradle ? PHASE_LABELS_C2C : PHASE_LABELS_STD;
  const totalCols = phaseCodes.length;
  const eolCols = isCradleToCradle ? 5 : 4;
  const gridStyle = { gridTemplateColumns: `repeat(${totalCols}, minmax(0, 1fr))` };

  const fetchData = useCallback(() => {
    if (!productId) {
      setIsLoading(false);
      return;
    }
    getLCAPhaseSelection({
      productId,
      setData: (data) => {
        if (data && data.lca_phases) {
          setPhases(data.lca_phases);
          setLcaScope(detectScope(data.lca_phases));
        } else {
          const initialPhases = {};
          LCA_PHASES_STD.forEach((p) => { initialPhases[p] = false; });
          setPhases(initialPhases);
          setLcaScope("custom");
        }
        setIsLoading(false);
      },
    });
  }, [productId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePhaseChange = (phase, checked) => {
    const newPhases = { ...phases, [phase]: checked };
    setPhases(newPhases);
    setLcaScope("custom");
    setHasChanges(true);
  };

  const handleScopeChange = (scope) => {
    let newPhases = {};
    if (scope === "custom") {
      // Reset all to false
      const allCodes = [...LCA_PHASES_STD, "C4_tech", "C4_bio"];
      allCodes.forEach((p) => { newPhases[p] = false; });
    } else if (scope === "cradle-to-cradle") {
      newPhases = { ...SCOPE_PRESETS["cradle-to-cradle"] };
    } else {
      const preset = SCOPE_PRESETS[scope] || {};
      LCA_PHASES_STD.forEach((p) => { newPhases[p] = !!preset[p]; });
    }
    setPhases(newPhases);
    setLcaScope(scope);
    setHasChanges(true);
  };

  const handleSave = () => {
    if (!productId) return;
    updateLCAPhaseSelection({
      productId,
      lca_phases: phases,
      callback: () => {
        setHasChanges(false);
        fetchData();
        window.dispatchEvent(new Event("lcaPhaseSelectionChanged"));
      },
      use_swal: true,
    });
  };

  if (isLoading) {
    return (
      <div className="lca-phase-selection-container">
        <div className="text-center">Loading...</div>
      </div>
    );
  }

  return (
    <div className="lca-phase-selection-container">
      <div className="lca-phase-selection-section">
        <h3 className="lca-phase-selection-title">
          {t("LCA_PHASE_SELECTION") || "Faseopdeling for LCA"}
        </h3>

        {/* Scope Selection – above table */}
        <div className="lca-phase-selection-scope-section">
          <div className="lca-phase-selection-scope-wrapper">
            <h4 className="lca-phase-selection-scope-title">This calculation covers:</h4>
            <div className="lca-phase-selection-scope-options">
              {SCOPE_OPTIONS.map((opt) => (
                <div
                  key={opt.value}
                  className={`lca-phase-selection-scope-option${lcaScope === opt.value ? " active" : ""}`}
                  onClick={() => handleScopeChange(opt.value)}
                >
                  <input
                    type="checkbox"
                    checked={lcaScope === opt.value}
                    onChange={() => handleScopeChange(opt.value)}
                    className="lca-phase-selection-scope-checkbox"
                  />
                  <span className="lca-phase-selection-scope-label">{opt.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Phase Table */}
        <div className="lca-phase-selection-grid-wrapper">
          {/* Main Category Headers */}
          <div className="lca-phase-selection-category-headers" style={gridStyle}>
            <div className="lca-phase-selection-category-header lca-phase-selection-category-product" style={{ gridColumn: "span 5" }}>
              <div className="lca-phase-selection-category-title">Product</div>
              <div className="lca-phase-selection-category-subtitle">Construction process</div>
            </div>
            <div className="lca-phase-selection-category-header lca-phase-selection-category-use" style={{ gridColumn: "span 7" }}>
              <div className="lca-phase-selection-category-title">Use</div>
            </div>
            <div className="lca-phase-selection-category-header lca-phase-selection-category-eol" style={{ gridColumn: `span ${eolCols}` }}>
              <div className="lca-phase-selection-category-title">
                {isCradleToCradle ? "Transition" : "End of life"}
              </div>
            </div>
            <div className="lca-phase-selection-category-header lca-phase-selection-category-beyond" style={{ gridColumn: "span 1" }}>
              <div className="lca-phase-selection-category-title lca-phase-selection-category-beyond-title">
                {isCradleToCradle ? "Next System Effects" : "Beyond system"}
              </div>
            </div>
          </div>

          {/* Phase Labels */}
          <div className="lca-phase-selection-labels-row" style={gridStyle}>
            {phaseCodes.map((phase) => {
              let categoryClass = "";
              if (["A1","A2","A3","A4","A5"].includes(phase)) categoryClass = "lca-phase-selection-label-product";
              else if (["B1","B2","B3","B4","B5","B6","B7"].includes(phase)) categoryClass = "lca-phase-selection-label-use";
              else if (["C1","C2","C3","C4","C4_tech","C4_bio"].includes(phase)) categoryClass = "lca-phase-selection-label-eol";
              else categoryClass = "lca-phase-selection-label-beyond";
              return (
                <div key={phase} className={`lca-phase-selection-label-cell ${categoryClass}`}>
                  <div className="lca-phase-selection-phase-label">{phaseLabels[phase]}</div>
                </div>
              );
            })}
          </div>

          {/* Phase Codes */}
          <div className="lca-phase-selection-codes-row" style={gridStyle}>
            {phaseCodes.map((phase) => (
              <div key={phase} className="lca-phase-selection-code-cell">
                <div className="lca-phase-selection-phase-code">{PHASE_CODE_DISPLAY[phase] || phase}</div>
              </div>
            ))}
          </div>

          {/* Checkboxes */}
          <div className="lca-phase-selection-checkboxes-row" style={gridStyle}>
            {phaseCodes.map((phase) => (
              <div key={phase} className="lca-phase-selection-checkbox-cell">
                <input
                  type="checkbox"
                  checked={phases[phase] || false}
                  onChange={(e) => handlePhaseChange(phase, e.target.checked)}
                  className="lca-phase-selection-checkbox"
                />
              </div>
            ))}
          </div>
        </div>

        {hasChanges && (
          <div className="lca-phase-selection-save-container">
            <button
              type="button"
              className="lca-phase-selection-save-btn"
              onClick={handleSave}
            >
              {t("SAVE") || "Save"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LCAPhaseSelection;
